import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { union, isEmpty, size, chain, findWhere, unique } from 'underscore';
import classnames from 'classnames';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { CommandBarButton } from '@fluentui/react/lib/Button';
import {
  SiteConfigContext,
  useIsSmartPageM365StockImageGalleryEnabled,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  useDefaultEditorStyle,
  getComponentById,
  formatImagesToSiteConfigImages,
  formatImagesToSiteConfigComponentConfig,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  EditImagesView,
  AddImagesPlaceholderWithPositioning,
} from '@bingads-webui-campaign-react/smart-component-image';

import { SelectImagesDialog } from './components/select-images-dialog';

const MaxImagesCount = 10;

const getStyles = () => ({
  imagesListLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '0 12px 2px 2px',
    marginBottom: 12,
  },
  addBtn: {
    backgroundColor: 'transparent',
    '&:enabled': {
      color: '#0063B1',
    },
  },
  deleteIcon: {
    color: '#EF0B0B',
  },
});

const UnStyledSmartGalleryEditor = ({
  i18n,
  classes,
  componentId,
  gotoSettings,
}) => {
  const {
    titleClassName,
    descriptionClassName,
    sectionTitleClassName,
    editorImgsContentClassName,
  } = useDefaultEditorStyle();
  const { siteConfig, dispatchSiteConfig } = useContext(SiteConfigContext);
  const [showSelectDialog, setShowSelectDialog] = useState(false);
  const [imageToReplace, setImageToReplace] = useState(null);
  const componentConfig = getComponentById(siteConfig, componentId) || {};
  const { photos: items = [] } = componentConfig;

  const isSmartPageM365StockImageGalleryEnabled = useIsSmartPageM365StockImageGalleryEnabled();

  const openDialog = useCallback(() => {
    setShowSelectDialog(true);
  }, []);

  const closeDialog = useCallback(() => {
    setImageToReplace(null);
    setShowSelectDialog(false);
  }, []);

  const updateImagesConfig = useCallback(({ imagesToAdd = [], imageIdsToRemove = [] }) => {
    dispatchSiteConfig({
      type: 'UPDATE_IMAGES',
      payload: { imagesToAdd, imageIdsToRemove },
    });
  }, [dispatchSiteConfig]);

  const updateGalleryConfig = useCallback((photos) => {
    dispatchSiteConfig({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload: { ...componentConfig, photos },
    });
  }, [dispatchSiteConfig, componentConfig]);

  const onUpdate = useCallback(({ imagesToAdd = [], imagesToRemove = [] }) => {
    const idsToRemove = chain(imagesToRemove).pluck('imageId').compact().value();
    const newAddedImageConfigs = formatImagesToSiteConfigComponentConfig(imagesToAdd);

    let newPhotos = [];
    const updatedExistingImages = items.filter(item =>
      !findWhere(imagesToRemove, { imageId: item.imageId }));

    // If we have imageToReplace, we are in single selection
    // newAddedImageConfigs could only have 0 or 1 length
    // We only need to replace it if we selected a new image
    if (imageToReplace && newAddedImageConfigs.length > 0) {
      newPhotos = [...updatedExistingImages];
      newPhotos.splice(imageToReplace.index, 0, newAddedImageConfigs[0]);
    } else { // !imageToReplace or newAddedImageConfigs.length === 0
      newPhotos = union(updatedExistingImages, newAddedImageConfigs);
    }

    updateImagesConfig({
      imagesToAdd: formatImagesToSiteConfigImages(imagesToAdd),
      imageIdsToRemove: unique(idsToRemove),
    });

    updateGalleryConfig(newPhotos);
    closeDialog();
  }, [updateImagesConfig, imageToReplace, updateGalleryConfig, closeDialog, items]);

  const onDelete = useCallback((_item, selectedIndex) => {
    const updated = [...items];
    const [deletedPhoto] = updated.splice(selectedIndex, 1);

    updateImagesConfig({
      imageIdsToRemove: deletedPhoto.imageId && [deletedPhoto.imageId],
    });

    updateGalleryConfig(updated);
  }, [items, updateGalleryConfig, updateImagesConfig]);

  const onReplace = useCallback((item, index) => {
    setImageToReplace({ item, index });
    openDialog();
  }, [openDialog]);

  return (
    <div>
      <h2 className={titleClassName}>
        {i18n.getString(_TL_('Gallery'))}
      </h2>
      <div className={descriptionClassName}>
        {i18n.getString(_TL_('Please select or upload up to {{max}} photos to add to your Gallery on your website.'), {
          max: MaxImagesCount,
        })}
      </div>
      <div className={editorImgsContentClassName}>
        <div className="delete-images-list">
          <div className={classes.imagesListLabel}>
            <span className={sectionTitleClassName}>{i18n.getString(_TL_('Your photos'))}</span>
            <CommandBarButton
              iconProps={{ iconName: 'Add' }}
              text={i18n.getString(_TL_('Add photos'))}
              onClick={openDialog}
              className={classnames(classes.addBtn, 'smart-page-add-images-btn')}
              disabled={items && items.length === MaxImagesCount}
            />
          </div>
          {
            !isEmpty(items) &&
            <EditImagesView
              items={items}
              onDelete={onDelete}
              onReplace={onReplace}
              i18n={i18n}
              imageRetryLimit={1}
              deleteOnly={!isSmartPageM365StockImageGalleryEnabled}
            />
          }
          {
            size(items) < MaxImagesCount &&
            <AddImagesPlaceholderWithPositioning
              i18n={i18n}
              itemCount={size(items)}
              onClick={openDialog}
            />
          }
        </div>
        {
          showSelectDialog &&
          <SelectImagesDialog
            onClose={closeDialog}
            onUpdate={onUpdate}
            i18n={i18n}
            existingImages={items}
            imageToReplace={imageToReplace ? imageToReplace.item : null}
            maxImagesCount={MaxImagesCount}
            gotoSettings={gotoSettings}
          />
        }
      </div>
    </div>
  );
};

UnStyledSmartGalleryEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    imagesListLabel: PropTypes.string.isRequired,
  }).isRequired,
  componentId: PropTypes.string.isRequired,
  gotoSettings: PropTypes.func.isRequired,
};

export const SmartGalleryEditor = withDefaultStyles(
  UnStyledSmartGalleryEditor,
  getStyles
);
